import React, { ReactNode } from 'react';
import { useIntl } from 'react-intl';

import MonetaryAmount from 'common/formatting/MonetaryAmount';

import { MetricsBlock } from './MetricsBlock';

type CampaignAnalyticsMetricsProps = {
  currency: Currency;
  title: string | ReactNode;
  headerAction?: ReactNode;
  colored?: boolean;
  isLoading?: boolean;
  isSingleCampaign: boolean;
  metrics: {
    totalSales: number;
    totalSpend: number;
    totalOrders: number;
    newCustomers?: number;
    returningCustomers?: number;
    averageBasketValue: number;
    averageBasketItemsCount: number;
    averageCostPerOrder: number;
  };
};

export const CampaignAnalyticsMetrics = ({
  colored,
  currency,
  headerAction,
  isLoading,
  isSingleCampaign,
  metrics: {
    averageBasketValue,
    averageBasketItemsCount,
    averageCostPerOrder,
    newCustomers,
    returningCustomers,
    totalOrders,
    totalSales,
    totalSpend,
  },
  title,
}: CampaignAnalyticsMetricsProps) => {
  const { formatMessage } = useIntl();

  return (
    <MetricsBlock
      title={title}
      headerAction={headerAction}
      colored={colored}
      isLoading={isLoading}
      primaryMetrics={[
        {
          title: formatMessage({ id: 'campaigns.analytics.total-sales' }),
          value: <MonetaryAmount amount={totalSales} currency={currency} />,
          tooltip: isSingleCampaign
            ? formatMessage({ id: 'campaigns.analytics.total-sales-tooltip-multiple' })
            : formatMessage({ id: 'campaigns.analytics.total-sales-tooltip-single' }),
        },
        {
          title: formatMessage({ id: 'campaigns.analytics.total-spend' }),
          value: <MonetaryAmount amount={totalSpend} currency={currency} />,
        },
        {
          title: formatMessage({ id: 'campaigns.analytics.total-orders' }),
          value: totalOrders,
          tooltip: isSingleCampaign
            ? formatMessage({ id: 'campaigns.analytics.total-orders-tooltip-multiple' })
            : formatMessage({ id: 'campaigns.analytics.total-orders-tooltip-single' }),
        },
      ]}
      secondaryMetrics={[
        ...(newCustomers !== undefined
          ? [{ title: formatMessage({ id: 'campaigns.analytics.new-customers' }), value: newCustomers }]
          : []),
        ...(returningCustomers !== undefined
          ? [{ title: formatMessage({ id: 'campaigns.analytics.returning-customers' }), value: returningCustomers }]
          : []),
        {
          title: formatMessage({ id: 'insights.performance.average-basket-size' }),
          value: <MonetaryAmount amount={averageBasketValue} currency={currency} />,
        },
        {
          title: formatMessage({ id: 'insights.performance.average-basket-size-items' }),
          value: averageBasketItemsCount,
        },
        {
          title: formatMessage({ id: 'campaigns.analytics.average-cost-per-order' }),
          value: <MonetaryAmount amount={averageCostPerOrder} currency={currency} />,
          tooltip: formatMessage({ id: 'campaigns.analytics.average-cost-per-order-tooltip' }),
        },
      ]}
    />
  );
};
